import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import {
  Plus,
  Search,
  FileDown,
  ChevronDown,
  Eye,
  Pencil,
  Trash2,
} from "lucide-react";

interface DocumentGroup {
  id: string;
  stt: number;
  groupName: string;
}

const mockData: DocumentGroup[] = [
  {
    id: "1",
    stt: 1,
    groupName: "Ban Lãnh đạo",
  },
  {
    id: "2",
    stt: 2,
    groupName: "Phòng Khoa học Công nghệ",
  },
  {
    id: "3",
    stt: 3,
    groupName: "Phòng Tài chính Kế toán",
  },
  {
    id: "4",
    stt: 4,
    groupName: "Trung tâm Thí nghiệm",
  },
  {
    id: "5",
    stt: 5,
    groupName: "Phòng Tổ chức Hành chính",
  },
];

const DocumentGroups: React.FC = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openMenuGroupId, setOpenMenuGroupId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteGroupId, setDeleteGroupId] = useState<string | null>(null);
  const [menuPlacement, setMenuPlacement] = useState<"top" | "bottom">(
    "bottom"
  );
  const [buttonPosition, setButtonPosition] = useState<{
    left: number;
    top: number;
    right: number;
    bottom: number;
  } | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const filteredData = mockData.filter((item) =>
    item.groupName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuGroupId) {
        const target = event.target as HTMLElement;
        if (
          !target.closest("[data-group-id]") &&
          !target.closest(".menu-container")
        ) {
          setOpenMenuGroupId(null);
        }
      }
    };

    const handleScroll = () => {
      if (openMenuGroupId) {
        setOpenMenuGroupId(null);
      }
    };

    if (openMenuGroupId) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openMenuGroupId, buttonPosition]);

  const handleDeleteConfirm = () => {
    if (deleteGroupId) {
      console.log("Deleting group:", deleteGroupId);
      setShowDeleteConfirm(false);
      setDeleteGroupId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteGroupId(null);
  };

  return (
    <PageShell title="Nhóm nhận văn bản">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Nhóm nhận văn bản
          </h1>
          <button
            onClick={() => navigate("/add-new-document-group")}
            className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Thêm mới Nhóm</span>
          </button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg p-4">
          <div className="flex items-center space-x-4">
            <div className="relative w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Tìm kiếm nhóm nhận văn bản"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
              Tìm kiếm
            </button>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                    STT
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Tên nhóm
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider w-24">
                    Chức năng
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 w-12">
                      {item.stt}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200">
                      <div className="font-medium text-[#337ab7] text-[14px] leading-tight">
                        {item.groupName}
                      </div>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap border-r border-gray-200 w-24">
                      <div
                        className="relative inline-block"
                        ref={(el) => {
                          dropdownRefs.current[item.id] = el;
                        }}
                      >
                        <button
                          data-group-id={item.id}
                          className="bg-[#337ab7] text-white px-2 py-1 rounded text-xs font-medium hover:bg-[#2e6da4] transition-colors flex items-center space-x-1"
                          onClick={(e) => {
                            const target = e.currentTarget as HTMLButtonElement;
                            const rect = target.getBoundingClientRect();
                            const estimatedMenuHeight = 200;
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const willOverflowBottom = spaceBelow < 250;
                            setMenuPlacement(
                              willOverflowBottom ? "top" : "bottom"
                            );
                            setButtonPosition({
                              left: rect.left,
                              top: rect.top,
                              right: rect.right,
                              bottom: rect.bottom,
                            });
                            setOpenMenuGroupId(
                              openMenuGroupId === item.id ? null : item.id
                            );
                          }}
                        >
                          <span>Chọn</span>
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        {openMenuGroupId === item.id &&
                          createPortal(
                            <div
                              className={
                                `fixed w-48 bg-white rounded-md shadow-lg border border-gray-200 z-[99999] ` +
                                (menuPlacement === "bottom"
                                  ? "mt-2"
                                  : "bottom-full mb-2")
                              }
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              style={
                                buttonPosition
                                  ? {
                                      left: `${buttonPosition.right - 200}px`,
                                      top:
                                        menuPlacement === "bottom"
                                          ? `${buttonPosition.bottom + 0}px`
                                          : `${buttonPosition.top - 180}px`,
                                      backgroundColor: "white",
                                      zIndex: 99999,
                                    }
                                  : {}
                              }
                            >
                              <div className="py-1 bg-white">
                                {[
                                  {
                                    label: "Chi tiết",
                                    Icon: Eye,
                                    colorClass: "text-blue-600",
                                  },
                                  {
                                    label: "Sửa",
                                    Icon: Pencil,
                                    colorClass: "text-amber-600",
                                  },
                                  {
                                    label: "Xóa",
                                    Icon: Trash2,
                                    colorClass: "text-red-600",
                                  },
                                ].map(({ label, Icon, colorClass }) =>
                                  label === "Chi tiết" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        setOpenMenuGroupId(null);
                                        navigate(
                                          `/document-groups/detail/${item.id}`
                                        );
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : label === "Sửa" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        setOpenMenuGroupId(null);
                                        navigate(
                                          `/document-groups/edit/${item.id}`
                                        );
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : label === "Xóa" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        setDeleteGroupId(item.id);
                                        setShowDeleteConfirm(true);
                                        setOpenMenuGroupId(null);
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : null
                                )}
                              </div>
                            </div>,
                            document.body
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">{filteredData.length}</span> kết
              quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                2
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                3
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Xác nhận xóa
              </h3>
              <p className="text-gray-600 mb-6">
                Bạn có chắc chắn muốn xóa nhóm này không? Hành động này không
                thể hoàn tác.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Hủy
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 transition-colors"
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default DocumentGroups;
